/** Reporting engine — aggregation helpers + metric/dimension registries.
 *
 * Ported from the dashboard mockup's data layer, typed and fed by the real
 * ReportingTicketRow[] API feed (the mock generator is dropped). Everything a
 * widget shows derives from these helpers, so a drill-down filter recomputes the
 * widget, the summary strip and the ticket list coherently.
 *
 * CSAT is the read-only Customer Thermometer: surfaced as a "% positive"
 * satisfaction share (positive ratings / rated), never a fabricated 1-5 score.
 */

import type { CsatTier, ReportingTicketRow } from "../services/api";

import type { BoardKey, DimensionKey, Filters, MetricKey, Widget } from "./types";

type Row = ReportingTicketRow;

export const C = {
  ink: "#29282E",
  green: "#00FF57",
  forest: "#103D13",
  purple: "#6600FF",
  orange: "#FF3E00",
  stone: "#877B6C",
  mud: "#595046",
  rule: "#e3dfd5",
  cream: "#F7F5F2",
} as const;

export const SANS = "'Figtree','Inter',sans-serif";
export const MONO = "'IBM Plex Mono',monospace";

export const PRIORITY_ORDER = ["Critical", "High", "Medium", "Low"] as const;
export const PRIORITY_COLOR: Record<string, string> = {
  Critical: C.orange,
  High: C.purple,
  Medium: C.forest,
  Low: C.stone,
};

/** Mean of the values, or null when there is nothing to average.
 *
 * Null, never 0: a month in which no response was ever measured has no average
 * response time, and "0m" claims an instant reply that never happened. Mirrors
 * the backend, which reports these as None and prints them as "n/a". */
const avg = (xs: number[]): number | null =>
  xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;

// Cohort predicates. The `??` fallbacks are load-bearing, not defensive noise:
// staging serves this bundle against the PROD backend, which does not publish the
// cohort fields until the day's release is published. Without them every widget
// would read 0 with no empty state, so the release sign-off dashboard would look
// broken. The fallbacks reproduce the pre-cohort behaviour exactly — the whole
// feed is the month, "completed" means status Closed — so an un-upgraded backend
// degrades to the old numbers rather than to nothing.
const isOpened = (x: Row): boolean => x.openedInPeriod ?? true;
const isCompleted = (x: Row): boolean => x.completedInPeriod ?? x.status === "Closed";
const isAutoGenerated = (x: Row): boolean => x.autoGenerated ?? false;
/** Day-of-month a closure should be plotted on. */
const closedOn = (x: Row): number => x.closedDay ?? x.day;

/** The opened cohort: tickets created inside the reporting month. Volume, every
 * breakdown/leaderboard and the ticket register are all measured over this. */
export const opened = (t: Row[]): Row[] => t.filter(isOpened);

/** The completed cohort: tickets finished inside the month, whenever they were
 * opened. This is what a service review means by "tickets completed". */
export const completed = (t: Row[]): Row[] => t.filter(isCompleted);

/** Drops the auto-generated alert queues without applying any cohort — the rows
 * that may stand as service evidence. They sit past their first-response due by
 * design, have no requester and fire overnight regardless of demand, so they
 * count as volume but never as response/SLA/CSAT/after-hours evidence. */
export const humanOnly = (t: Row[]): Row[] => t.filter((x) => !isAutoGenerated(x));

/** Opened tickets a person actually raised. Mirrors the backend's `human_raised`. */
const human = (t: Row[]): Row[] => humanOnly(opened(t));

/** The mirror's placeholder bucket, as the backend labels a NULL dimension. */
const UNSPECIFIED = "Unspecified";

/** Whether a leaderboard entry is a person a client-facing panel may name.
 *
 * Mirrors `named_people` / `client_requesters` in services/reporting.py, which
 * do the same job for the report PDF.
 *
 * "Unspecified" among named colleagues reads as a person who does not exist, and
 * unlike a category it cannot be renamed into something meaningful. iQuest's own
 * entries additionally come out of the CLIENT's requester board: tickets iQuest
 * raises on a client's behalf carry an iQuest contact, and "iQuest (Insourced)"
 * ranked among a client's own people is internal plumbing showing through. The
 * tickets still count towards volume — they are just not attributed to the
 * customer's staff. Staff boards keep their iQuest names, which is the point of
 * those panels. */
const isNamedPerson = (name: string): boolean => name !== UNSPECIFIED;
const isClientRequester = (name: string): boolean =>
  isNamedPerson(name) && !name.trim().toLowerCase().startsWith("iquest");

/** True when the customer actually answered the survey.
 *
 * Keyed on the raw temperature, not on the classified tier: a rating we failed to
 * map is still a rating the customer sent, and treating it as "no response" is
 * exactly the bug that hid 19% of them. */
export const isRated = (x: Row): boolean => !!x.csat;

/** The rated cohort: completed tickets carrying a Customer Thermometer rating.
 *
 * The satisfaction figures measure these, so the CSAT drill-down lists them too.
 * Listing every completed ticket would bury the handful that were rated among
 * hundreds that were not. */
export const rated = (t: Row[]): Row[] => humanOnly(completed(t)).filter(isRated);

/** The four survey ratings, best to worst. Mirrors services/csat_scale.py. */
export const CSAT_TIER_ORDER = ["gold", "green", "yellow", "red"] as const;
export const CSAT_TIER_LABELS: Record<CsatTier, string> = {
  gold: "Fantastic",
  green: "Good",
  yellow: "Neutral",
  red: "Review",
};
/** The survey's own colours, so the dashboard reads like the email the customer
 *  answered: gold star, green tick, amber face, red triangle. */
export const CSAT_TIER_COLOR: Record<CsatTier, string> = {
  gold: "#C8A227",
  green: "#2E7D32",
  yellow: "#E0A82E",
  red: "#FF3E00",
};

/** Tier for a row, falling back to the raw temperature when the backend predates
 *  csatTier (staging runs against the prod backend). */
export function tierOf(x: Row): CsatTier | null {
  if (x.csatTier) return x.csatTier;
  const key = (x.csat ?? "").trim().toLowerCase();
  if (key.startsWith("gold")) return "gold";
  if (key.startsWith("green")) return "green";
  if (key.startsWith("yellow")) return "yellow";
  if (key.startsWith("red")) return "red";
  return null;
}

/** Counts per rating tier over whatever rows are handed in, plus the responses
 *  that carry a temperature we could not classify. */
export function csatTierCounts(t: Row[]): {
  counts: Record<CsatTier, number>;
  total: number;
  unclassified: number;
} {
  const counts: Record<CsatTier, number> = { gold: 0, green: 0, yellow: 0, red: 0 };
  let total = 0;
  let unclassified = 0;
  for (const x of t) {
    if (!isRated(x)) continue;
    total += 1;
    const tier = tierOf(x);
    if (tier) counts[tier] += 1;
    else unclassified += 1;
  }
  return { counts, total, unclassified };
}

/** Cohort-free statistics: they measure exactly the rows handed to them.
 *
 * The drill-down needs these. It has ALREADY narrowed the feed to one cohort
 * (`cohortForWidget`) and then to the user's filters, so re-applying a cohort
 * inside the stat would silently drop rows the popout is listing — reporting, in
 * the worst case, "SLA met 100%" over an empty judged set beside a table of 60
 * tickets. `A.*` below are these same stats with the dashboard's cohort applied.
 */
export const S = {
  backlog: (t: Row[]): number => t.filter((x) => x.status !== "Closed").length,
  avgResponse: (t: Row[]): number | null =>
    avg(t.filter((x) => x.firstResponseMins != null).map((x) => x.firstResponseMins as number)),
  avgResolution: (t: Row[]): number | null =>
    avg(t.filter((x) => x.resolutionHours != null).map((x) => x.resolutionHours as number)),
  /** Null when nothing in `t` carries an SLA verdict — "no evidence" is not 100%. */
  slaCompliance: (t: Row[]): number | null => {
    const judged = t.filter((x) => x.slaResponseMet != null);
    if (!judged.length) return null;
    return judged.filter((x) => x.slaResponseMet).length / judged.length;
  },
  csat: (t: Row[]): { fraction: number; n: number } => {
    const rated = t.filter((x) => x.csatBucket != null);
    const positive = rated.filter((x) => x.csatBucket === "positive").length;
    return { fraction: rated.length ? positive / rated.length : 0, n: rated.length };
  },
};

export const A = {
  opened: (t: Row[]): number => opened(t).length,
  closed: (t: Row[]): number => completed(t).length,
  backlog: (t: Row[]): number => S.backlog(opened(t)),
  afterHours: (t: Row[]): number => human(t).filter((x) => x.afterHours).length,
  avgResponse: (t: Row[]): number | null => S.avgResponse(human(t)),
  avgResolution: (t: Row[]): number | null => S.avgResolution(human(t)),
  // Null, never 1. A gauge that renders "no judged tickets" as a target-meeting
  // 100% invents the best possible score out of no evidence, and contradicted
  // the report, which prints n/a from the same data (issue #97).
  slaCompliance: (t: Row[]): number | null => S.slaCompliance(human(t)),
  // CSAT runs on the human-raised tickets COMPLETED in the month, not the ones
  // opened in it: a rating cannot exist until the work is finished, so on the
  // opened cohort a month kept gaining ratings after it closed and a complaint
  // could land in a review two months back. Mirrors aggregate_metrics server-side
  // so the dashboard and the PDF report the same figure.
  csat: (t: Row[]): { fraction: number; n: number } => S.csat(humanOnly(completed(t))),
  rows: (t: Row[], key: DimensionKey): { name: string; count: number }[] => {
    const m = new Map<string, number>();
    for (const x of opened(t)) m.set(x[key], (m.get(x[key]) ?? 0) + 1);
    const arr = [...m.entries()].map(([name, count]) => ({ name, count }));
    if (key === "priority") {
      const rank = (n: string): number => {
        const i = (PRIORITY_ORDER as readonly string[]).indexOf(n);
        return i === -1 ? 99 : i;
      };
      arr.sort((a, b) => rank(a.name) - rank(b.name));
    } else {
      arr.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }
    return arr;
  },
  // "Top requesters" runs on the human pool — an alert queue has no requester, so
  // it would otherwise put an "Unspecified" placeholder at rank 1. Staff
  // leaderboards keep the full opened cohort: someone did work the alerts.
  //
  // Entries are dropped BEFORE the widget takes its top N, so removing one
  // promotes the next real person rather than leaving the panel a row short.
  leaderboard: (t: Row[], key: BoardKey): { name: string; tickets: number }[] => {
    const m = new Map<string, number>();
    for (const x of key === "contact" ? human(t) : opened(t)) {
      m.set(x[key], (m.get(x[key]) ?? 0) + 1);
    }
    const belongs = key === "contact" ? isClientRequester : isNamedPerson;
    return [...m.entries()]
      .map(([name, tickets]) => ({ name, tickets }))
      .filter((r) => belongs(r.name))
      .sort((a, b) => b.tickets - a.tickets);
  },
  // Closures are plotted on the day they happened (closedDay), not on the day the
  // ticket was opened, so the two bars describe the same day's work.
  daily: (t: Row[], days: number): { d: number; o: number; c: number }[] => {
    const arr = Array.from({ length: days }, (_, i) => ({ d: i + 1, o: 0, c: 0 }));
    for (const x of t) {
      if (isOpened(x)) {
        const idx = x.day - 1;
        if (idx >= 0 && idx < days) arr[idx].o += 1;
      }
      if (isCompleted(x)) {
        const idx = closedOn(x) - 1;
        if (idx >= 0 && idx < days) arr[idx].c += 1;
      }
    }
    return arr;
  },
};

export type MetricKind = "count" | "min" | "hour" | "pct";

export interface MetricDef {
  label: string;
  kind: MetricKind;
  better: "up" | "down" | "flat";
  target?: number;
  /** Null when the month carries no evidence for this metric. Every renderer
   *  must show that as "no data", never as a zero or a full score. */
  compute: (t: Row[]) => number | null;
}

export const METRICS: Record<MetricKey, MetricDef> = {
  opened: { label: "Tickets opened", kind: "count", better: "flat", compute: A.opened },
  closed: {
    label: "Tickets completed",
    kind: "count",
    better: "up",
    compute: A.closed,
  },
  avgResponse: {
    label: "Avg first response",
    kind: "min",
    better: "down",
    target: 30,
    compute: A.avgResponse,
  },
  avgResolution: {
    label: "Avg resolution",
    kind: "hour",
    better: "down",
    target: 8,
    compute: A.avgResolution,
  },
  sla: {
    // Named for the question it answers, so it cannot be read as the same
    // figure the Response times page reports. That one is a rolling 30 days
    // counted on the day each ticket was ANSWERED; this is the calendar month
    // counted on the day each was RAISED. Different questions, so the two will
    // not match and should not look like they are trying to (issue #99).
    label: "Answered on time",
    kind: "pct",
    better: "up",
    target: 0.95,
    compute: A.slaCompliance,
  },
  csat: {
    label: "Customer satisfaction",
    kind: "pct",
    better: "up",
    target: 0.9,
    // Null with no ratings at all. The fraction is 0 in that case, which would
    // print a 0% satisfaction score off nobody's opinion (issue #97).
    compute: (t) => {
      const c = A.csat(t);
      return c.n ? c.fraction : null;
    },
  },
  afterHours: {
    label: "After-hours tickets",
    kind: "count",
    better: "flat",
    compute: A.afterHours,
  },
  backlog: {
    label: "Open backlog",
    kind: "count",
    better: "down",
    target: 0,
    compute: A.backlog,
  },
};

// Minute-valued metrics (avg first response) read in minutes under an hour and
// in hours at/above it, per ops feedback: a "45 min" response stays "45m", a
// "90 min" one reads as "1.5h". Value + unit must agree, so both consult this.
const MIN_AS_HOURS_THRESHOLD = 60;

export function metricUsesHours(key: MetricKey, v: number): boolean {
  return METRICS[key].kind === "min" && v >= MIN_AS_HOURS_THRESHOLD;
}

/** How a metric reads on screen. "n/a" for no evidence, matching the report. */
export function fmtMetric(key: MetricKey, v: number | null): string {
  if (v == null) return "n/a";
  const k = METRICS[key].kind;
  if (k === "pct") return String(Math.round(v * 100));
  if (metricUsesHours(key, v)) return (Math.round((v / 60) * 10) / 10).toFixed(1);
  if (k === "min") return String(Math.round(v));
  if (k === "hour") return (Math.round(v * 10) / 10).toFixed(1);
  return String(Math.round(v));
}

// `v` lets a minute-valued metric report "h" once it crosses the hour; pass it
// wherever the unit sits next to a live value (omit it for static labels/targets).
export function unitMetric(key: MetricKey, v?: number | null): string {
  // Explicit null means "no value", so there is no unit to put beside "n/a".
  // Undefined means the caller wants the static unit (e.g. next to a target).
  if (v === null) return "";
  if (v !== undefined && metricUsesHours(key, v)) return "h";
  return { count: "", min: "m", hour: "h", pct: "%" }[METRICS[key].kind];
}

export interface DimensionDef {
  label: string;
  /** The dimension named as a thing rather than as a panel title, for the
   *  drill-down header when one of its rows is picked ("Issue type · Alerts").
   *  `label` reads "By issue type", which does not fit in that sentence. */
  rowLabel: string;
  key: DimensionKey;
  colorBy?: boolean;
  accent?: string;
}

export const DIMENSIONS: Record<DimensionKey, DimensionDef> = {
  priority: { label: "By priority", rowLabel: "Priority", key: "priority", colorBy: true },
  issueType: { label: "By issue type", rowLabel: "Issue type", key: "issueType", accent: C.forest },
  subType: { label: "By sub-category", rowLabel: "Sub-category", key: "subType", accent: C.purple },
  source: { label: "By source", rowLabel: "Source", key: "source", accent: C.mud },
};

export interface BoardDef {
  label: string;
  /** As above: "Top requesters" is a panel title, "Requester" names the row. */
  rowLabel: string;
  key: BoardKey;
  accent: string;
}

export const LEADERBOARDS: Record<BoardKey, BoardDef> = {
  contact: { label: "Top requesters", rowLabel: "Requester", key: "contact", accent: C.forest },
  engineer: { label: "Top staff members", rowLabel: "Staff member", key: "engineer", accent: C.purple },
};

/** Which cohort a set of rows is being viewed as. Determines what "day" means:
 * the day a ticket was raised, or the day it was finished. */
export type Cohort = "opened" | "completed" | "rated";

/** The day-of-month a row sits on WITHIN a cohort.
 *
 * A completed-cohort row may have been opened in an earlier month, so its
 * `day` (the open day) can exceed the reporting month's length — filtering or
 * sorting the completed cohort by it drops tickets the tile counted and orders
 * the rest by an irrelevant date. */
export function cohortDay(x: Row, cohort: Cohort = "opened"): number {
  // "rated" is the completed cohort narrowed to answered surveys, so it dates the
  // same way: by the day the work finished.
  return cohort === "completed" || cohort === "rated" ? closedOn(x) : x.day;
}

export function applyFilters(
  tickets: Row[],
  f?: Partial<Filters>,
  cohort: Cohort = "opened",
): Row[] {
  if (!f) return tickets;
  return tickets.filter((t) => {
    if (f.priority?.length && !f.priority.includes(t.priority)) return false;
    if (f.issueType?.length && !f.issueType.includes(t.issueType)) return false;
    if (f.subType?.length && !f.subType.includes(t.subType)) return false;
    if (f.source?.length && !f.source.includes(t.source)) return false;
    if (f.status?.length && !f.status.includes(t.status)) return false;
    const day = cohortDay(t, cohort);
    if (f.dayFrom != null && day < f.dayFrom) return false;
    if (f.dayTo != null && day > f.dayTo) return false;
    return true;
  });
}

/** Distinct values present for a dimension (priority returned in severity order). */
export function distinctValues(
  tickets: Row[],
  key: DimensionKey | "status",
): string[] {
  const present = [...new Set(tickets.map((t) => t[key]))];
  if (key === "priority") {
    return (PRIORITY_ORDER as readonly string[]).filter((p) => present.includes(p));
  }
  return present.sort((a, b) => a.localeCompare(b));
}

export function countActiveFilters(f?: Partial<Filters>): number {
  if (!f) return 0;
  let n = 0;
  for (const k of ["priority", "issueType", "subType", "source", "status"] as const) {
    n += f[k]?.length ?? 0;
  }
  return n;
}

/** Drill-down seed: open the Focus Explorer pre-filtered to what the widget
 * represents (ops feedback: clicking "Open backlog" should start on open
 * tickets, not the whole month). Only widgets with a single natural slice seed
 * a filter; "Tickets opened" is already the full month, and gauges/breakdowns
 * (SLA, CSAT, response time, priority split, leaderboards) span everything, so
 * they seed nothing. "Tickets completed" needs no status seed either — its
 * cohort (see cohortForWidget) is already the completed one. A filter the user
 * explicitly saved onto the widget still wins over this seed. */
export function seedFiltersForWidget(w: Widget, tickets: Row[]): Partial<Filters> {
  if (w.metric === "backlog") {
    const open = distinctValues(tickets, "status").filter((s) => s !== "Closed");
    return open.length ? { status: open } : {};
  }
  return {};
}

/** Which cohort a widget's drill-down lists. "Tickets completed" counts work
 * finished this month (some of it opened earlier), so its underlying tickets are
 * the completed cohort; every other widget is measured over the month's opened
 * tickets and lists those. Keeps the popout's row count equal to the number on
 * the tile the user clicked.
 *
 * Customer satisfaction lists the RATED cohort: completed tickets that actually
 * carry a Customer Thermometer rating. The figure is computed over the rated
 * responses, so listing every completed ticket would bury the handful that were
 * answered among hundreds that were not. */
export function cohortForWidget(w: Widget): Cohort {
  if (w.metric === "csat" || w.type === "csat") return "rated";
  return w.metric === "closed" ? "completed" : "opened";
}

/** The rows in a cohort. */
export function cohortRows(cohort: Cohort, tickets: Row[]): Row[] {
  if (cohort === "rated") return rated(tickets);
  return cohort === "completed" ? completed(tickets) : opened(tickets);
}
